import { useMatches } from "@tanstack/react-router";

import { removeTrailingSlash } from "lib/utils";
import { BreadcrumbItem } from "./SimpleBreadcrumb";

export interface LabelOverride {
  label: string;
  isLoading?: boolean;
}

/**
 * Auto-generates breadcrumb items from the TanStack Router match chain.
 *
 * Each matched route that has `staticData.breadcrumbLabel` (or `pageName` as fallback) becomes a breadcrumb entry.
 * Routes with empty pageName (layout routes) are skipped.
 * The last entry is rendered without a link (current page).
 *
 * For dynamic segments (e.g. the bid name at /bids/$id), pass overrides keyed by
 * the route's `pathname` at that match level:
 *
 * @example
 * // On /bids/123/bid_responses, useMatches() gives:
 * //   root → /bids (label: "Bids") → /bids/123 (no label) → /bids/123/bid_responses (label: "Bid responses")
 * //
 * // Provide the bid name for the /bids/123 level:
 * const items = useRouteBreadcrumbs({
 *   [`/bids/${bidId}`]: { label: bid?.name || "Bid", isLoading }
 * });
 * // Result: [{ label: "Bids", path: "/bids" }, { label: "My Bid", path: "/bids/123" }, { label: "Bid responses" }]
 */
export function useRouteBreadcrumbs(overrides?: Record<string, LabelOverride>): BreadcrumbItem[] {
  const matches = useMatches();

  const items: BreadcrumbItem[] = [];

  for (const match of matches) {
    // Normalise pathname so layout routes (/bids/123) and their index routes
    // (/bids/123/) are treated as the same path.
    const pathname = removeTrailingSlash(match.pathname);
    const override = overrides?.[pathname];
    const label =
      override?.label ?? match.staticData?.breadcrumbLabel ?? match.staticData?.pageName;

    if (!label) continue;

    // Layout routes and their index routes share the same pathname — deduplicate
    // by merging into the existing entry rather than adding a second one.
    // Overrides always take priority over static labels.
    const prev = items.at(-1);
    if (prev && prev.path === pathname) {
      if (override) {
        prev.label = label;
        prev.isLoading = override.isLoading;
      }
      continue;
    }

    items.push({
      label,
      path: pathname,
      isLoading: override?.isLoading,
    });
  }

  // Last item is the current page — no link
  if (items.length > 0) {
    items[items.length - 1] = {
      ...items[items.length - 1],
      path: undefined,
      isLoading: undefined,
    };
  }

  return items;
}
